// The Notebook's cells, as a pure fold over the chat's plugin events (#1869).
//
// `pluginEvents` is the same list the chat's render plugins fold, live and
// after a reload, so the chat and the Notebook cannot disagree about what was
// made. Each cell is keyed by its event's id (pluginEventId, the key the chat
// cards use). An event whose `revises` names an earlier cell is that cell's
// revision, not a new cell; the stored NotebookState says which revisions the
// person confirmed or undid, and holds their notes and unsent edits.
import type { AgentSSEEvent } from '../../../agent'
import { pluginEventId } from '../../../agent/pluginEventId'
import type { NotebookCell, NotebookCellKind, NotebookRevision, NotebookState } from './notebookTypes'

const KIND_BY_EVENT: Record<string, NotebookCellKind> = {
  table_rendered: 'table',
  chart_rendered: 'chart',
  composite_table_rendered: 'composite',
  warehouse_query: 'query',
}

/** The event types that make a Notebook cell. */
export const NOTEBOOK_CELL_EVENTS = Object.keys(KIND_BY_EVENT)

const str = (v: unknown): string => (typeof v === 'string' ? v : '')

const FALLBACK_TITLE: Record<NotebookCellKind, string> = {
  table: 'Table',
  chart: 'Chart',
  composite: 'Composite table',
  query: 'Query',
  script: 'Script',
  note: 'Note',
}

function titleOf(kind: NotebookCellKind, data: Record<string, unknown>): string {
  return str(data.title) || (kind === 'query' ? str(data.connection_name) : '') || FALLBACK_TITLE[kind]
}

/** Every script a composite's provenance names: the run receipt's, then each cell's script refs. */
function scriptPaths(data: Record<string, unknown>): string[] {
  const provenance = data.provenance as { run?: { script?: unknown }; cells?: Record<string, { refs?: unknown }> } | undefined
  if (!provenance || typeof provenance !== 'object') return []
  const paths: string[] = []
  const add = (p: unknown) => {
    const s = str(p).trim()
    if (s && !paths.includes(s)) paths.push(s)
  }
  add(provenance.run?.script)
  for (const cell of Object.values(provenance.cells ?? {})) {
    if (!Array.isArray(cell?.refs)) continue
    for (const ref of cell.refs as Array<{ type?: unknown; path?: unknown }>) {
      if (ref?.type === 'script') add(ref.path)
    }
  }
  return paths
}

/** The file in the chat's folder an event's result was saved to, if any. */
function fileOf(data: Record<string, unknown>): string {
  const dataset = str(data.datasetName)
  return dataset ? `data/${dataset}.json` : str(data.saved_path)
}

interface Draft {
  id: string
  kind: NotebookCellKind
  data: Record<string, unknown>
  revisions: NotebookRevision[]
}

export function deriveNotebookCells(pluginEvents: AgentSSEEvent[], state: NotebookState): NotebookCell[] {
  const drafts: Draft[] = []
  const byId = new Map<string, Draft>()
  // A revision's own event id → the cell it belongs to, so a revision of a
  // revision lands on the original cell.
  const ownerOf = new Map<string, Draft>()
  // Keys seen per event type and tool call: the ordinal of the plugins' fallback key.
  const seen = new Map<string, Set<string>>()

  for (const event of pluginEvents) {
    const kind = KIND_BY_EVENT[event.type]
    if (!kind) continue
    const data = (event.data ?? {}) as Record<string, unknown>
    // The query plugin ignores an event without a connection, so the Notebook does too.
    if (kind === 'query' && !data.connection_id) continue
    const bucket = `${event.type}\u0000${str(data.toolCallId) || str(data.tool_use_id) || str(data.messageId)}`
    const keys = seen.get(bucket) ?? new Set<string>()
    seen.set(bucket, keys)
    const id = pluginEventId(data, kind === 'query' ? 0 : keys.size)
    if (!id) continue
    keys.add(id)

    // The same event again (a replay of a live event) replaces what it made.
    const existing = byId.get(id)
    if (existing) {
      existing.kind = kind
      existing.data = data
      continue
    }
    const owner = ownerOf.get(id)
    if (owner) {
      owner.revisions = owner.revisions.map((r) => (r.eventId === id ? { ...r, kind, data } : r))
      continue
    }

    const revises = str(data.revises)
    const target = revises ? (byId.get(revises) ?? ownerOf.get(revises)) : undefined
    if (target) {
      target.revisions.push({ eventId: id, kind, data, decision: state.decisions[id] })
      ownerOf.set(id, target)
      continue
    }

    const draft: Draft = { id, kind, data, revisions: [] }
    drafts.push(draft)
    byId.set(id, draft)
  }

  const cells: NotebookCell[] = []
  const scriptsPlaced = new Set<string>()
  for (const draft of drafts) {
    let kind = draft.kind
    let data = draft.data
    let pending: NotebookRevision | undefined
    let shownAt = -1
    draft.revisions.forEach((revision, i) => {
      if (revision.decision === 'confirmed') {
        kind = revision.kind
        data = revision.data
        pending = undefined
        shownAt = i
      } else if (!revision.decision) {
        pending = revision
      }
    })
    // Every revision after the shown one was undone or is undecided: one saved
    // to the same file has replaced what the cell shows.
    const file = fileOf(data)
    const fileReplaced = !!file && draft.revisions.slice(shownAt + 1).some((r) => fileOf(r.data) === file)
    const tabular = kind === 'table' || kind === 'chart'
    cells.push({
      id: draft.id,
      kind,
      title: titleOf(kind, data),
      data,
      toolCallId: str(draft.data.toolCallId) || undefined,
      datasetName: str(data.datasetName) || undefined,
      revisions: draft.revisions,
      pending,
      edit: state.edits[draft.id],
      fileReplaced: fileReplaced || undefined,
      needsRun: tabular && !data.platinumData ? true : undefined,
    })
    if (kind !== 'composite') continue
    for (const path of scriptPaths(data)) {
      if (scriptsPlaced.has(path)) continue
      scriptsPlaced.add(path)
      cells.push({
        id: `script:${path}`,
        kind: 'script',
        title: path,
        data: { path },
        revisions: [],
        sourceCellId: draft.id,
      })
    }
  }

  return withNotes(cells, state)
}

/** Put each note after the cell (or note) it names, in the order they were written. */
function withNotes(cells: NotebookCell[], state: NotebookState): NotebookCell[] {
  if (state.notes.length === 0) return cells
  const notes = [...state.notes].sort((a, b) => a.created_at - b.created_at)
  const anchored = new Map<string, NotebookCell[]>()
  const noteCells = notes.map((note): NotebookCell => ({
    id: note.id,
    kind: 'note',
    title: FALLBACK_TITLE.note,
    data: {},
    revisions: [],
    text: note.text,
    sent: note.sent_at !== undefined && note.sent_at !== null,
  }))
  const known = new Set([...cells.map((c) => c.id), ...notes.map((n) => n.id)])
  // A composite's scripts stay with it: a note after the composite goes after its last script.
  const groupEnd = new Map<string, string>()
  for (const c of cells) if (c.kind === 'script' && c.sourceCellId) groupEnd.set(c.sourceCellId, c.id)
  notes.forEach((note, i) => {
    const anchor = note.after === null ? null : (groupEnd.get(note.after) ?? note.after)
    const key = anchor === null ? '' : known.has(anchor) && anchor !== note.id ? anchor : '\u0000end'
    anchored.set(key, [...(anchored.get(key) ?? []), noteCells[i]])
  })

  const out: NotebookCell[] = []
  const placed = new Set<string>()
  const emitNotesAfter = (key: string) => {
    for (const note of anchored.get(key) ?? []) {
      if (placed.has(note.id)) continue
      placed.add(note.id)
      out.push(note)
      emitNotesAfter(note.id)
    }
  }
  emitNotesAfter('')
  for (const cell of cells) {
    out.push(cell)
    emitNotesAfter(cell.id)
  }
  emitNotesAfter('\u0000end')
  // A cycle of notes naming each other has no way in: put them at the end.
  for (const note of noteCells) if (!placed.has(note.id)) out.push(note)
  return out
}

/** Whether the chat has run a data query (a warehouse query cell). The
 *  composer's Notebook button shows only then (Adam, 5 Nov 2026). */
export function hasDataQuery(cells: NotebookCell[]): boolean {
  return cells.some((c) => c.kind === 'query')
}
